import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import RootLayout from './pages/layout';
import AdminLayout from './pages/admin/layout';

// Player-facing pages
import Home from './pages/index';
import About from './pages/about/index';
import Cards from './pages/cards/index';
import Collection from './pages/collection/index';
import Earn from './pages/earn/index';
import Events from './pages/events/index';
import EventsClassic from './pages/events/classic/[id]/index';
import EventsMain from './pages/events/main/[id]/index';
import EventsMainUndercard from './pages/events/main/[id]/undercards/[undercardId]/index';
import EventsPropBoost from './pages/events/prop-boost/[id]/index';
import Faq from './pages/faq/index';
import Leaderboards from './pages/leaderboards/index';
import Marketplace from './pages/marketplace/index';
import MyEvents from './pages/my-events/index';
import PacksOpen from './pages/packs/[id]/open/index';
import Players from './pages/players/index';
import Privacy from './pages/privacy/index';
import Profile from './pages/profile/index';
import Store from './pages/store/index';
import Terms from './pages/terms/index';
import { ContestDetail } from './pages/ContestDetail';

// Auth — Jest sign-in only. Email/password, Google OAuth, and the register/
// forgot/reset surfaces were removed; the Jest client authenticates exclusively
// via window.JestSDK.getPlayerSigned() → POST /api/auth/jest.
import Login from './pages/login/index';

// Preview / marketing
import PreviewEarnCoins from './pages/preview/earn-coins/index';
import PreviewPlayerProps from './pages/preview/player-props/index';
import PreviewUnifiedCard from './pages/preview/unified-card/index';
import PreviewSwipePicks from './pages/preview/swipe-picks/index';

// Admin
import AdminDashboard from './pages/admin/index';
import AdminCards from './pages/admin/cards/index';
import AdminEvents from './pages/admin/events/index';
import AdminEventsNew from './pages/admin/events/new/index';
import AdminEventDetail from './pages/admin/events/[id]/index';
import AdminMainEvents from './pages/admin/main-events/index';
import AdminMainEventsNew from './pages/admin/main-events/new/index';
import AdminMainEventDetail from './pages/admin/main-events/[id]/index';
import AdminMainEventUndercard from './pages/admin/main-events/[id]/undercards/[undercardId]/index';
import AdminCardLeagueNew from './pages/admin/card-leagues/new/index';
import AdminPropBoost from './pages/admin/prop-boost/index';
import AdminQuests from './pages/admin/quests/index';
import AdminRewards from './pages/admin/rewards/index';
import AdminRewardClaims from './pages/admin/rewards/claims/index';
import AdminUsers from './pages/admin/users/index';
import AdminUserDetail from './pages/admin/users/[id]/index';

/**
 * Route tree for the Jest client.
 *
 * Paths mirror the Next.js webapp's URL conventions so any tracking links,
 * referral codes, deep links from emails, and the JestSDK entry payload all
 * keep working without translation. Dynamic segments from Next's `[id]`
 * directory naming become `:id` (react-router-dom) here.
 *
 * Layout hierarchy:
 *   RootLayout (Navigation + Footer + Providers)
 *     └── AdminLayout (admin chrome) for /admin/*
 *     └── everything else mounted as direct children
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Home /> },
      { path: 'about', element: <About /> },
      { path: 'cards', element: <Cards /> },
      { path: 'collection', element: <Collection /> },
      { path: 'earn', element: <Earn /> },
      { path: 'faq', element: <Faq /> },
      { path: 'leaderboards', element: <Leaderboards /> },
      { path: 'marketplace', element: <Marketplace /> },
      { path: 'my-events', element: <MyEvents /> },
      { path: 'players', element: <Players /> },
      { path: 'privacy', element: <Privacy /> },
      { path: 'profile', element: <Profile /> },
      { path: 'terms', element: <Terms /> },

      // Auth
      { path: 'login', element: <Login /> },

      // Events
      { path: 'events', element: <Events /> },
      { path: 'contests/:contestId', element: <ContestDetail /> },
      { path: 'events/classic/:id', element: <EventsClassic /> },
      { path: 'events/main/:id', element: <EventsMain /> },
      { path: 'events/main/:id/undercards/:undercardId', element: <EventsMainUndercard /> },
      { path: 'events/prop-boost/:id', element: <EventsPropBoost /> },

      // Packs
      { path: 'packs/:id/open', element: <PacksOpen /> },

      // Store
      { path: 'store', element: <Store /> },

      // Preview
      { path: 'preview/earn-coins', element: <PreviewEarnCoins /> },
      { path: 'preview/player-props', element: <PreviewPlayerProps /> },
      { path: 'preview/unified-card', element: <PreviewUnifiedCard /> },
      { path: 'preview/swipe-picks', element: <PreviewSwipePicks /> },

      // Admin — nested under its own layout (auth gating + header chrome)
      {
        path: 'admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminDashboard /> },
          { path: 'cards', element: <AdminCards /> },
          { path: 'events', element: <AdminEvents /> },
          { path: 'events/new', element: <AdminEventsNew /> },
          { path: 'events/:id', element: <AdminEventDetail /> },
          { path: 'main-events', element: <AdminMainEvents /> },
          { path: 'main-events/new', element: <AdminMainEventsNew /> },
          { path: 'main-events/:id', element: <AdminMainEventDetail /> },
          { path: 'main-events/:id/undercards/:undercardId', element: <AdminMainEventUndercard /> },
          { path: 'card-leagues/new', element: <AdminCardLeagueNew /> },
          { path: 'prop-boost', element: <AdminPropBoost /> },
          { path: 'quests', element: <AdminQuests /> },
          { path: 'rewards', element: <AdminRewards /> },
          { path: 'rewards/claims', element: <AdminRewardClaims /> },
          { path: 'users', element: <AdminUsers /> },
          { path: 'users/:id', element: <AdminUserDetail /> },
        ],
      },

      // Catch-all: entry-payload deep links land in the memory router
      // verbatim (getRouteFromEntryPayload passes payload.route through), so
      // an unrouted path must degrade to Home — without this the router
      // renders its default error page over the whole app at boot.
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];
